// A count sheet is dated evidence. Importing it never mutates today's product stock.
const PlummeInventoryCountSheet = (() => {
  const headers = () => [t('prd.colProductId'), t('prd.itemNo'), t('prd.colProductName'), t('prd.colInventoryAccount'), t('prd.colStockToday'), t('prd.colCountedQuantity'), t('prd.colCostPerUnit'), t('prd.colTotalCostValue'), t('prd.colNote')];
  const round2 = (value) => Math.round((value + Number.EPSILON) * 100) / 100;
  const amount = (value) => {
    if (value === '' || value == null) return null;
    const parsed = Number(String(value).trim().replace(/\s/g, '').replace(',', '.'));
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
  };
  const goods = (products) => (products || []).filter((item) => item.type === 'Vare' || item.kind === 'physical')
    .filter((item) => !['passiv', 'archived'].includes(item.status));
  const templateRows = (products, year) => goods(products, year).map((item) => [
    item.id || '', item.nr || item.itemNumber || '', item.name || '', item.inventoryAccountCode || '1460',
    item.stock ?? item.stockQuantity ?? 0, item.stock ?? item.stockQuantity ?? 0, item.averageInventoryUnitCost ?? item.costPrice ?? 0, null, item.description || '',
  ]);
  const exportTemplate = async (filename, products, year, details = {}) => {
    await loadXLSX();
    if (!window.XLSX) throw new Error(t('prd.excelExportUnavailable'));
    const rows = templateRows(products, year);
    const sheet = window.XLSX.utils.aoa_to_sheet([headers(), ...rows]);
    rows.forEach((_row, index) => { const row = index + 2; sheet[`H${row}`] = { t: 'n', f: `IF(OR(F${row}="",G${row}=""),"",F${row}*G${row})` }; });
    sheet['!cols'] = [{ wch: 26 }, { wch: 14 }, { wch: 34 }, { wch: 14 }, { wch: 19 }, { wch: 19 }, { wch: 22 }, { wch: 20 }, { wch: 36 }];
    const info = window.XLSX.utils.aoa_to_sheet([
      [t('prd.countSheetTitle'), t('prd.countSheetIntro')],
      [t('prd.countSheetDate'), details.countDate || ''], [t('prd.countSheetCountedBy'), details.countedBy || ''], [t('prd.countSheetReference'), details.countReference || ''], [t('prd.countSheetSignature'), ''],
      [t('prd.countSheetNoteLabel'), t('prd.countSheetNote')],
    ]);
    info['!cols'] = [{ wch: 20 }, { wch: 85 }];
    const workbook = window.XLSX.utils.book_new();
    window.XLSX.utils.book_append_sheet(workbook, sheet, t('prd.countSheetTitle'));
    window.XLSX.utils.book_append_sheet(workbook, info, t('prd.countSheetInfoTab'));
    window.XLSX.writeFile(workbook, `${filename}.xlsx`);
  };
  const parseMatrix = (matrix, products, year) => {
    if (!Array.isArray(matrix) || matrix.length < 2 || headers().some((header, index) => String(matrix[0]?.[index] || '').trim() !== header)) throw new Error(t('prd.countSheetWrongTemplate'));
    const available = goods(products, year);
    const matched = new Set();
    const lines = [];
    const errors = [];
    matrix.slice(1).forEach((row, index) => {
      if (!row || row.every((value) => value === '' || value == null)) return;
      const [id, nr, name, account, , counted, unitCost] = row;
      if (counted === '' || counted == null) return;
      const byId = available.find((item) => String(item.id) === String(id));
      const byNumber = !byId && nr ? available.filter((item) => String(item.nr || item.itemNumber || '') === String(nr)) : [];
      const product = byId || (byNumber.length === 1 ? byNumber[0] : null);
      const quantity = amount(counted), parsedCost = amount(unitCost), cost = quantity === 0 && parsedCost == null ? 0 : parsedCost;
      if (!product || matched.has(product.id) || quantity == null || cost == null || !/^14\d{2}$/.test(String(account || ''))) {
        errors.push(t('prd.countSheetRowError', 'Rad {row}: Kontroller vare, lagerkonto, opptalt antall og varekost.', { row: index + 2 }));
        return;
      }
      matched.add(product.id);
      lines.push({ productId: String(product.id), itemNumber: String(nr || ''), name: String(name || product.name), accountCode: String(account), quantity, unitCost: cost, totalCost: round2(quantity * cost) });
    });
    if (errors.length) throw new Error(errors.slice(0, 3).join(' '));
    if (!lines.length) throw new Error(t('prd.countSheetNoneCounted'));
    return lines;
  };
  const parseFile = async (file, products, year) => {
    const name = String(file?.name || '').toLowerCase();
    if (!name.endsWith('.xlsx')) throw new Error(t('prd.countSheetOnlyXlsx'));
    await loadXLSX();
    if (!window.XLSX) throw new Error(t('prd.excelImportUnavailable'));
    const workbook = window.XLSX.read(await file.arrayBuffer(), { type: 'array' });
    const sheet = workbook.Sheets[t('prd.countSheetTitle')];
    if (!sheet) throw new Error(t('prd.countSheetTabNotFound', 'Fant ikke arket {name}.', { name: t('prd.countSheetTitle') }));
    return parseMatrix(window.XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: '' }), products, year);
  };
  const categoryTotals = (lines, categoryRows) => {
    const byCode = new Map((categoryRows || []).map((row) => [String(row.inventoryAccountCode), row.key]));
    const totals = new Map();
    for (const line of lines || []) {
      const key = byCode.get(String(line.accountCode));
      if (!key) throw new Error(t('prd.countSheetUnmappedAccount', 'Lagerkonto {account} kan ikke fordeles automatisk. Kontroller kontoene i gjennomgangen.', { account: line.accountCode }));
      totals.set(key, round2((totals.get(key) || 0) + line.totalCost));
    }
    return totals;
  };
  return { headers, goods, templateRows, exportTemplate, parseMatrix, parseFile, categoryTotals };
})();
window.PlummeInventoryCountSheet = PlummeInventoryCountSheet;
